/* Need to eslint no-undef otherwise eslint does not see ApplePayJS global type; TS can see that type */

/* eslint-disable no-undef */
import { ActionCreator, AnyAction } from 'redux'
import { ThunkAction as ReduxThunkAction } from 'redux-thunk'

import { actionTypes } from 'actions/actionTypes'
import statusActions from 'actions/status'
import { trackCheckoutError } from 'actions/tracking'
import { clickApplePayPay, identifyApplePayEligibility } from 'actions/trackingKeys'
import { checkoutActions } from 'routes/Checkout/checkoutActions'
import { phoneValidator } from 'routes/Checkout/checkoutFormUtils'
import {
  convertApplePayTokenToCheckoutToken,
  validateApplePaySession as fetchValidateApplePaySession,
} from 'routes/Checkout/checkoutPaymentsApis'
// eslint-disable-next-line import/named
import { hasCheckoutError } from 'routes/Checkout/checkoutSelectors'
import {
  ApplePayState,
  createApplePaySession,
  createSignupRequest,
} from 'routes/Checkout/utils/applePaySessionUtils'
import { Pricing } from 'routes/Menu/domains/pricing'
import { getBasketPostcode } from 'selectors/basket'

type Store = { form: any }
type ThunkAction<T = void> = ReduxThunkAction<T, Store, any, AnyAction>
type ThunkActionCreator<T = void> = ActionCreator<ThunkAction<T>>

export const saveApplePayDeliveryInstruction: ActionCreator<AnyAction> = (
  deliveryInstruction: string,
) => ({
  type: actionTypes.SAVE_APPLE_PAY_DELIVERY_INSTRUCTION,
  deliveryInstruction,
})

export const saveApplePayDeliveryInstructionCustom: ActionCreator<AnyAction> = (
  deliveryInstructionCustom: string,
) => ({
  type: actionTypes.SAVE_APPLE_PAY_DELIVERY_INSTRUCTION_CUSTOM,
  deliveryInstructionCustom,
})

export const saveApplePayDeliveryAddress: ActionCreator<AnyAction> = ({
  addressLines,
  administrativeArea,
  postalCode,
  locality,
}: ApplePayJS.ApplePayPaymentContact) => ({
  type: actionTypes.SAVE_APPLE_PAY_DELIVERY_ADDRESS,
  deliveryAddress: {
    name: 'My Address',
    line1: addressLines?.[0],
    line2: addressLines?.[1],
    line3: addressLines?.[2] ?? '',
    town: locality,
    county: administrativeArea,
    postcode: postalCode,
  },
})

export const saveApplePayPersonalInfo: ActionCreator<AnyAction> = ({
  givenName,
  familyName,
  phoneNumber,
}: ApplePayJS.ApplePayPaymentContact) => ({
  type: actionTypes.SAVE_APPLE_PAY_PERSONAL_INFO,
  personalInfo: {
    firstName: givenName,
    lastName: familyName,
    phoneNumber,
  },
})

export const saveApplePayCheckoutToken: ActionCreator<AnyAction> = (token: string) => ({
  type: actionTypes.SAVE_APPLE_PAY_CHECKOUT_TOKEN,
  token,
})

/**
 * Tracks in snowplow if browser has Apple Pay enabled.
 */
export const trackApplePayEligibility: ActionCreator<AnyAction> = (
  isApplePayEligible: boolean,
) => ({
  type: actionTypes.IDENTIFY_APPLE_PAY_ELIGIBILITY,
  trackingData: {
    actionType: identifyApplePayEligibility,
    eligibility: isApplePayEligible,
  },
})

export const trackClickPayWithApplePay: ActionCreator<AnyAction> = () => ({
  type: actionTypes.CLICK_PAY_WITH_APPLE_PAY,
  trackingData: { actionType: clickApplePayPay },
})

/**
 * Validates Apple Pay session and completes Apple Pay merchant validation.
 */
export const validateApplePaySession: ThunkActionCreator =
  (event: ApplePayJS.ApplePayValidateMerchantEvent, session: ApplePaySession) =>
  async (dispatch) => {
    const { validationURL } = event
    try {
      const { data } = await fetchValidateApplePaySession(validationURL)
      session.completeMerchantValidation(data)
    } catch (e) {
      dispatch(statusActions.error(actionTypes.APPLE_PAY_MERCHANT_VALIDATION_ERROR, true))
      dispatch(
        trackCheckoutError(
          actionTypes.APPLE_PAY_MERCHANT_VALIDATION_ERROR,
          true,
          'validateApplePaySession',
        ),
      )
      session.abort()
    }
  }

export const validateShippingContact: ThunkActionCreator<ApplePayError[]> =
  (shippingContact: ApplePayJS.ApplePayPaymentContact) => (_dispatch, getState) => {
    const errors: ApplePayError[] = []

    const { postalCode = '', countryCode, phoneNumber } = shippingContact

    const wizardPostcode = (getBasketPostcode(getState() as any) as string)
      .replace(/\s+/g, '')
      .toUpperCase()
    const applePayPostcode = postalCode.replace(/\s+/g, '').toUpperCase()

    // From developer.apple.com:
    // For Canada and United Kingdom, a redacted shipping address excludes the last three characters of the postal code.
    // The full postal code is provided after the user authorizes the transaction.
    if (!wizardPostcode.startsWith(applePayPostcode)) {
      errors.push(
        new ApplePayError(
          'shippingContactInvalid',
          'postalCode',
          "A postcode in this delivery address doesn't match the postcode entered in a previous step",
        ),
      )
    }

    if (countryCode !== 'GB') {
      errors.push(new ApplePayError('shippingContactInvalid', 'countryCode'))
    }

    if (typeof phoneNumber !== 'undefined') {
      const errorMessage = phoneValidator(phoneNumber)

      if (errorMessage) {
        errors.push(new ApplePayError('shippingContactInvalid', 'phoneNumber', errorMessage))
      }
    }

    return errors
  }

/**
 * Completes checkout payment as if payment was authorized.
 * @param event - Apple Pay payment authorized event
 * @param session - Apple Pay session
 * @param pricing - checkout pricing info (total price, etc.)
 */
export const checkoutApplePayPayment: ThunkActionCreator =
  (event: ApplePayJS.ApplePayPaymentAuthorizedEvent, session: ApplePaySession, pricing: Pricing) =>
  async (dispatch, getState) => {
    const errors = dispatch(validateShippingContact(event.payment.shippingContact))

    if (errors.length > 0) {
      session.completePayment({
        status: ApplePaySession.STATUS_FAILURE,
        errors,
      })

      return
    }

    dispatch(saveApplePayDeliveryAddress(event.payment.shippingContact))
    dispatch(saveApplePayPersonalInfo(event.payment.shippingContact))

    try {
      const applePayToken = event.payment.token.paymentData
      const { data } = await convertApplePayTokenToCheckoutToken(applePayToken)
      dispatch(saveApplePayCheckoutToken(data.token))
    } catch {
      dispatch(statusActions.error(actionTypes.APPLE_PAY_TOKEN_CONVERSION_ERROR, true))
      dispatch(
        trackCheckoutError(
          actionTypes.APPLE_PAY_TOKEN_CONVERSION_ERROR,
          true,
          'checkoutApplePayPayment',
        ),
      )
      session.completePayment(ApplePaySession.STATUS_FAILURE)

      return
    }

    await dispatch(checkoutActions.checkoutSignup({ pricing }))

    const state = getState()
    const isFinishedWithError = hasCheckoutError(state)

    session.completePayment(
      isFinishedWithError ? ApplePaySession.STATUS_FAILURE : ApplePaySession.STATUS_SUCCESS,
    )
  }

/**
 * Creates and starts Apple Pay payment session.
 * @param pricing - checkout pricing info (total price, etc.).
 * @param deliveryDate - user selected date
 * @param deliveryTime - user selected time range
 */
export const initApplePaySession: ThunkActionCreator =
  (pricing: Pricing | null, deliveryDate: string, deliveryTime: string) => (dispatch, getState) => {
    const state = getState() as unknown as ApplePayState
    if (pricing === null) return

    const signupRequest = createSignupRequest(state, pricing, deliveryDate, deliveryTime)
    if (signupRequest === null) return

    const session = createApplePaySession(signupRequest, {
      onvalidatemerchant: (event) => dispatch(validateApplePaySession(event, session)),
      onpaymentauthorized: (event) => dispatch(checkoutApplePayPayment(event, session, pricing)),
      onshippingcontactselected: (event) => {
        const errors = dispatch(validateShippingContact(event.shippingContact))
        session.completeShippingContactSelection({
          newTotal: {
            type: 'final',
            label: 'Total',
            amount: pricing.total as string,
          },
          errors,
        })
      },
    })
  }
